import { Component, HostListener, Inject, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import * as moment from 'moment';
import { BehaviorSubject, from } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CreationEditionDialogComponent } from 'src/app/marketing/mass-messaging/creation-edition-dialog/creation-edition-dialog.component';
import { MassMessaging, MassMessagingStatusEnum } from 'src/app/marketing/shared';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Config } from 'src/app/shared/components/errors-placeholder-cell/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { FacebookService, WhatsappService } from 'src/app/shared/strategies/channels';
import { MassMessagingService } from './mass-messaging.service';
import { RunningMassMessagingVolumesRefresher } from './shared/running-mass-messaging-volumes-refresher.class';

@Component({
  selector: 'app-mass-messaging',
  templateUrl: './mass-messaging.component.html',
  styleUrls: ['./mass-messaging.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MassMessagingComponent implements OnDestroy {
  @Input() campaignScenarios: MassMessaging[];
  @Input() segments: any[];
  maxHeight = 500;
  dataSource = [];
  MassMessagingStatusEnum = MassMessagingStatusEnum;

  displayedColumnsForScheduled = [
    'account',
    'name',
    'numberOfMessagesSuccessfullySent',
    'numberOfMessagesNotSent',
    'numberOfErrors',
    'remainingNumberOfMessagesToSend',
    'startTime',
    'creatorEmail',
    'updateTime',
    'scheduledTime',
    'status',
    'actions',
  ];

  displayedColumnsForSend = [
    'account',
    'name',
    'numberOfMessagesSuccessfullySent',
    'numberOfMessagesNotSent',
    'numberOfErrors',
    'remainingNumberOfMessagesToSend',
    'read',
    'startTime',
    'creatorEmail',
    'updateTime',
    'status',
    'actions',
  ];

  config: Config = {
    domainEndpoint: 'mass-messaging',
    titleKey: 'mass_messaging/details/title',
    descriptionKey: 'mass_messaging/details/description',
    confirmBtnKey: 'mass_messaging/details/export',
    displayedColumns: [
      { name: 'date', type: 'date' },
      { name: 'actionId' },
      { name: 'userName' },
      { name: 'userExternalId' },
      { name: 'taskLink', type: 'link' },
      { name: 'actionErrorId' },
    ],
  };

  massMessagingRefresher;

  sortActive = 'updateTime';
  sortDirection: 'asc' | 'desc' = 'desc';
  marketingSites;

  siteKey = `mass-messaging-filtered-site-${this.alcmeonContextService.companyId}`;
  sitesCtrl = new FormControl([]);

  dataSourceBS: BehaviorSubject<MassMessaging[]> = new BehaviorSubject([]);

  campaignToSendSet: Set<number> = new Set();
  campaignToScheduledSet: Set<number> = new Set();

  /**
   * Allows to have a full responsive table by taking the available height. The overflow of the table will be visible by using a scroll.
   * @remarks 200 considers the headers (48px) and the filters (152px). As the target is the routed component, the header is ignored.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.maxHeight = Math.max(document.getElementById('right-side-content').clientHeight - 188, 500);
  }

  @ViewChild(MatTable) table: MatTable<MassMessaging>;

  constructor(
    public massMessagingService: MassMessagingService,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private dialog: MatDialog,
    private errorHandlingService: ErrorHandlingService,
    private sitesService: SitesService,
    private customRouter: CustomRouter,
    whatsappService: WhatsappService,
    facebookService: FacebookService,
    @Inject('MassMessagingFactory') private massMessagingFactory: any, // Once v1 is deleted, move function in `MassMessagingService`.
    @Inject('SiteFilterHelperFactory') private siteFilterHelperFactory: any,
    @Inject('AudienceFactory') private audienceFactory: any,
    @Inject('DropdownItemsFactory') private dropdownItemsFactory: any,
    @Inject('$stateParams') private $stateParams: any
  ) {
    if (this.alcmeonContextService.isAlcmeonUser()) {
      this.config.displayedColumns.push({
        name: 'errorDetails',
        action: element => this.showErrorDetails(element),
      });
    }

    this.sitesCtrl.valueChanges.subscribe(() => this.updateDataSource(this.massMessagingService.tabIndex));

    this.basicService.showSpinner();
    this.massMessagingService.tabIndex = 2;
    this.massMessagingService
      .getCampaigns()
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe((campaigns: MassMessaging[]) => {
        this.dataSource = campaigns;
        this.updateDataSource(this.massMessagingService.tabIndex);

        campaigns.forEach(campaign => {
          const { read } = this.massMessagingService.getDataFromAudienceFilters(campaign);
          if (!campaign.tmpFields) {
            campaign.tmpFields = {};
          }
          if (read) {
            campaign.tmpFields.read = read;
          }

          const { audience_filters } = campaign;

          const account = this.sitesService.sitesV2Map.get(+audience_filters.accounts[0]);
          if (account) {
            campaign.tmpFields.account = account;
            if ([whatsappService, facebookService].every(service => service.getType() !== account?.type)) {
              campaign.read = this.basicService.translate('not_applicable');
            }
          }
        });
        this.onResize();
        this.massMessagingService.segments = this.segments;
        this.massMessagingService.campaignScenarios = this.campaignScenarios;

        const marketingSites = this.siteFilterHelperFactory.filterSitesThatHaveCapability(
          this.sitesService.sitesV2,
          this.alcmeonContextService.self,
          CapabilityIdEnum.Marketing
        );
        this.marketingSites = this.dropdownItemsFactory.buildSiteItems(marketingSites);
        this.massMessagingFactory.addUIFields(this.dataSource, marketingSites);
        this.refreshVolumesForPausedCampaigns();
        this.massMessagingRefresher = new RunningMassMessagingVolumesRefresher(
          this.alcmeonContextService.getCompany(),
          this.dataSource,
          this.massMessagingFactory
        );
        this.massMessagingRefresher.refreshVolumesOfCreatedAndRunningCampaign(); // computing volumes for each running campaign
        this.sortData({ active: this.sortActive, direction: this.sortDirection });

        // Technical limitation due to volumes refreshing in real time and we do not know th related states of them.
        // Quick win, wait for a second -> data are loading it is not frustrating.
        setTimeout(() => {
          const { status } = this.$stateParams;
          const urlStatus = +status;
          if (urlStatus >= 0 && urlStatus < 3) {
            this.updateDataSource(+urlStatus);
          }
        });
      });
  }

  /**
   * We do not want to use the auto refresh mechanism for the `On hold` state as it was paused by human.
   * It would not have sense to do it when a campaign is not running.
   */
  private refreshVolumesForPausedCampaigns() {
    this.dataSource
      .filter((massMessaging: MassMessaging) => massMessaging.status === MassMessagingStatusEnum.ON_HOLD_STATUS)
      .forEach((massMessaging: MassMessaging) => {
        this.massMessagingFactory
          .getMassMessagingVolumes(this.alcmeonContextService.companyId, massMessaging)
          .then(({ data }: { data: MassMessaging }) => {
            this.massMessagingFactory.setUIVolumeFieldsFromRawData(massMessaging, data);
          });
      });
  }

  editMassMessaging(massMessaging: MassMessaging) {
    const index = this.dataSource.findIndex(({ id }) => id === massMessaging.id);
    const audienceSites = massMessaging.audience_filters.accounts;
    const entrypointsDropdownItems = this.massMessagingFactory.buildMassMessagingEntrypointsDropdownItems(
      this.campaignScenarios,
      audienceSites
    );

    const dialogRef = this.dialog.open(CreationEditionDialogComponent, {
      data: {
        massMessaging: { ...massMessaging },
        audienceDropdownItems: this.audienceFactory.buildDropdownItems(this.sitesService.sitesV2, this.segments),
        massMessagingEntrypointsDropdownItems: entrypointsDropdownItems,
        isNewMassMessaging: false,
      },
    });
    dialogRef.afterClosed().subscribe(editedMassMessaging => {
      if (editedMassMessaging) {
        from(this.massMessagingFactory.updateMassMessaging(this.alcmeonContextService.companyId, editedMassMessaging))
          .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
          .subscribe({
            next: ({ data }: { data: MassMessaging }) => {
              const { updateTime, isScheduled, scheduledTime } = data;
              this.dataSource[index] = { ...editedMassMessaging, updateTime, isScheduled, scheduledTime };
              this.table.renderRows();
              if (editedMassMessaging.scheduledTime && !editedMassMessaging.isScheduled) {
                this.updateDataSource(0);
              } else if (!editedMassMessaging.scheduledTime && editedMassMessaging.isScheduled) {
                this.updateDataSource(1);
              }
            },
          });
      }
    });
  }

  deleteMassMessaging(massMessaging: MassMessaging) {
    const index = this.dataSource.indexOf(massMessaging);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('mass_messaging/confirm_mass_messaging_deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.massMessagingService
          .deleteMassMessaging(massMessaging)
          .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
          .subscribe({
            next: () => {
              this.dataSource.splice(index, 1);
              this.table.renderRows();
            },
          });
      }
    });
  }

  doActionOnMassMessaging(massMessaging: MassMessaging) {
    switch (massMessaging.status) {
      case MassMessagingStatusEnum.CREATED_STATUS:
        massMessaging.disableActionButton = true;
        this.sendMassMessaging(massMessaging);
        break;
      case MassMessagingStatusEnum.STARTED_STATUS:
      case MassMessagingStatusEnum.WAITING_FOR_QUOTA:
        this.pauseMassMessaging(massMessaging);
        break;
      case MassMessagingStatusEnum.ON_HOLD_STATUS:
        this.resumeMassMessaging(massMessaging);
        break;
      default:
        break;
    }
  }

  private sendMassMessaging(massMessaging: MassMessaging) {
    this.massMessagingFactory
      .askForSend(massMessaging)
      .then(() => {
        from(this.massMessagingFactory.sendMassMessaging(this.alcmeonContextService.companyId, massMessaging))
          .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
          .subscribe({
            next: ({ data }: { data: MassMessaging }) => {
              massMessaging.status = data.status;
              this.dialog.open(ConfirmDialogComponent, {
                data: {
                  description: this.basicService.translate('mass_messaging/messages_being_sent', {
                    n: `${massMessaging.remainingNumberOfMessagesToSend}`,
                  }),
                  confirmBtnText: this.basicService.translate('close'),
                },
              });
              this.massMessagingRefresher.restartRefreshIfNeeded();
              massMessaging.disableActionButton = false;
            },
          });
      })
      .catch(() => {
        massMessaging.disableActionButton = false;
      });
  }

  private pauseMassMessaging(massMessaging: MassMessaging) {
    from(this.massMessagingFactory.pauseMassMessaging(this.alcmeonContextService.companyId, massMessaging))
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
      .subscribe({
        next: ({ data }: { data: MassMessaging }) => {
          massMessaging.status = data.status;
          this.dialog.open(ConfirmDialogComponent, {
            data: {
              description: this.basicService.translate('mass_messaging/campaign_paused'),
              confirmBtnText: this.basicService.translate('close'),
            },
          });
          // no need to stop refresher, it will stop by itself if there's no running campaign
        },
      });
  }

  private resumeMassMessaging(massMessaging: MassMessaging) {
    from(this.massMessagingFactory.resumeMassMessaging(this.alcmeonContextService.companyId, massMessaging))
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
      .subscribe({
        next: ({ data }: { data: MassMessaging }) => {
          massMessaging.status = data.status;
          this.dialog.open(ConfirmDialogComponent, {
            data: {
              description: this.basicService.translate('mass_messaging/campaign_resumed'),
              confirmBtnText: this.basicService.translate('close'),
            },
          });
          this.massMessagingRefresher.restartRefreshIfNeeded();
        },
      });
  }

  getActionButtonLabel(massMessaging: MassMessaging) {
    let action = null;
    switch (massMessaging.status) {
      case MassMessagingStatusEnum.CREATED_STATUS:
        action = 'send_button';
        break;
      case MassMessagingStatusEnum.STARTED_STATUS:
      case MassMessagingStatusEnum.WAITING_FOR_QUOTA:
        action = 'pause_button';
        break;
      case MassMessagingStatusEnum.ON_HOLD_STATUS:
        action = 'resume_button';
        break;
    }
    return this.basicService.translate(`mass_messaging/${action}`);
  }

  showActionButton(massMessaging: MassMessaging) {
    return [
      MassMessagingStatusEnum.CREATED_STATUS,
      MassMessagingStatusEnum.STARTED_STATUS,
      MassMessagingStatusEnum.ON_HOLD_STATUS,
      MassMessagingStatusEnum.WAITING_FOR_QUOTA,
    ].includes(massMessaging.status);
  }

  showEditButton(massMessaging: MassMessaging) {
    return [MassMessagingStatusEnum.CREATED_STATUS].includes(massMessaging.status);
  }

  sortData($event) {
    const { active, direction } = $event;
    this.sortActive = active;
    this.sortDirection = direction;
    const listSorted = this.dataSource.sort((a: MassMessaging, b: MassMessaging) => {
      switch (active) {
        case 'account':
          return (a.tmpFields.account.siteName || '').localeCompare(b.tmpFields.account.siteName);
        case 'numberOfMessagesNotSent':
        case 'numberOfErrors':
        case 'remainingNumberOfMessagesToSend':
        case 'read':
          return a[active] - b[active];
        case 'startTime':
        case 'updateTime':
        case 'scheduledTime':
          return moment(a[active], 'YYYY-MM-DD HH:mm:ss').isAfter(moment(b[active], 'YYYY-MM-DD HH:mm:ss')) ? 1 : -1;
        case 'name':
        case 'numberOfMessagesSuccessfullySent':
        case 'creatorEmail':
          return (a[active] || '').localeCompare(b[active]);
        case 'status':
          return this.massMessagingService.getStatusLabel(a).localeCompare(this.massMessagingService.getStatusLabel(b));
        default:
          break;
      }
    });
    if (direction === 'desc') {
      listSorted.reverse();
    }
    this.dataSource = listSorted;
    this.table?.renderRows();
  }

  updateDataSource(tabIndex: number) {
    this.massMessagingService.tabIndex = tabIndex;
    let statusesDisplayed = [];
    if (!tabIndex || tabIndex === 1) {
      statusesDisplayed = [
        MassMessagingStatusEnum.CREATED_STATUS,
        MassMessagingStatusEnum.STARTED_STATUS,
        MassMessagingStatusEnum.ON_HOLD_STATUS,
        MassMessagingStatusEnum.WAITING_FOR_QUOTA,
      ];
    } else {
      statusesDisplayed = [MassMessagingStatusEnum.COMPLETED_STATUS, MassMessagingStatusEnum.CANCELLED_STATUS];
    }
    const sitesFiltered = this.sitesCtrl.value;
    this.dataSource.forEach((campaign: MassMessaging) => {
      const { id, isScheduled, audience_filters } = campaign;
      const completed = [MassMessagingStatusEnum.COMPLETED_STATUS, MassMessagingStatusEnum.CANCELLED_STATUS].includes(
        campaign.status
      );
      campaign.hide =
        (tabIndex !== 2 && completed) ||
        (isScheduled && !tabIndex) ||
        (!isScheduled && tabIndex === 1) ||
        (sitesFiltered.length && !sitesFiltered.includes(audience_filters.accounts[0])) ||
        !statusesDisplayed.some(status => status === campaign.status);
      if (completed) {
        this.campaignToSendSet.delete(id);
        this.campaignToScheduledSet.delete(id);
      } else if (isScheduled) {
        this.campaignToSendSet.delete(id);
        this.campaignToScheduledSet.add(id);
      } else {
        this.campaignToSendSet.add(id);
        this.campaignToScheduledSet.delete(id);
      }
      if (sitesFiltered && sitesFiltered.length && !sitesFiltered.includes(audience_filters.accounts[0])) {
        this.campaignToSendSet.delete(id);
        this.campaignToScheduledSet.delete(id);
      }
    });

    this.dataSourceBS.next(this.dataSource);
  }

  showErrorDetails = ({ errorDetails }) => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'messaging-campaign-error-details',
      data: {
        description: errorDetails,
        confirmBtnText: this.basicService.translate('close'),
      },
    });
    dialogRef.afterClosed().subscribe();
  };

  openMassMessaging({ id }) {
    this.customRouter.go('auth.alcmeon.fuse.mass-messaging.details', { id });
  }

  ngOnDestroy() {
    try {
      this.massMessagingRefresher.cancelNextAutoRefreshIfAny();
    } catch {
      // Do nothing in case of issue.
    }
  }
}
